import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScanFace } from "lucide-react";
import { NeuCard } from "../../components/neu/Neu";
import { DocAlert } from "./DocAlert";
import { RemoteBadge } from "./RemoteBadge";
import { roleLabel } from "../../utils/role";
import { roleRank, roleBanner } from "../../utils/roleMeta";

// "متصل الآن" = آخر نشاط خلال 5 دقائق (نفس عتبة الخادم). last_seen_at يأتي ضمن قائمة الموظفين.
const ONLINE_MS = 5 * 60 * 1000;
const isOnline = (e) => {
  const ts = e.last_seen_at ? Date.parse(e.last_seen_at) : 0;
  return !!ts && Date.now() - ts < ONLINE_MS;
};

// Cards are pure navigation: editing and deleting live inside the employee page itself.
export const EmployeeList = ({ employees, branchNames }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (employees.length === 0) return <p className="text-sm text-muted-foreground">{t("app.none")}</p>;
  const sorted = [...employees].sort((a, b) => roleRank(a) - roleRank(b));

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="employee-list">
      {sorted.map((e) => (
        <NeuCard key={e.id} className="animate-rise-in overflow-hidden p-0" data-testid={`employee-card-${e.id}`}>
          <div
            className={`flex items-center justify-between px-4 py-1.5 text-xs font-bold text-white ${roleBanner(e)}`}
            data-testid={`employee-role-banner-${e.id}`}
          >
            <span>{roleLabel(e.hierarchy_role || e.role, t)}</span>
            <RemoteBadge workMode={e.work_mode} since={e.remote_since} />
          </div>
          <button
            type="button"
            data-testid={`employee-open-${e.id}`}
            onClick={() => navigate(`/employees/${e.id}`)}
            className="w-full p-5 text-start transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-xl font-bold hover:text-primary">{e.name}</p>
                <p className="truncate text-sm text-muted-foreground">{e.email}</p>
              </div>
              <div className="relative shrink-0">
                {e.avatar ? (
                  <img
                    src={e.avatar}
                    alt={e.name}
                    className={`h-12 w-12 rounded-full object-cover shadow-neu-sm ${isOnline(e) ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-background" : ""}`}
                    data-testid={`employee-avatar-${e.id}`}
                  />
                ) : (
                  <ScanFace className={`h-5 w-5 ${e.face_enrolled ? "text-success" : "text-muted-foreground/60"}`} />
                )}
                {isOnline(e) ? (
                  <span className="absolute bottom-0 end-0 flex h-3.5 w-3.5" data-testid={`employee-online-${e.id}`}>
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                  </span>
                ) : null}
              </div>
            </div>
            {e.status === "onboarding" ? (
              <p className="mt-2 text-xs font-bold text-warning" data-testid={`employee-onboarding-${e.id}`}>
                {t("requests.incompleteProfile")}
              </p>
            ) : null}
            <p className="mt-3 text-sm text-muted-foreground">
              {(e.branch_ids || []).map((id) => branchNames[id]).filter(Boolean).join(" · ") || "—"}
            </p>
            <DocAlert personal={e.personal} testId={`employee-doc-alert-${e.id}`} />
            <p className="mt-4 text-xs font-bold text-primary">{t("employees.openCard")}</p>
          </button>
        </NeuCard>
      ))}
    </div>
  );
};
